import type { ErrorEvent } from '@sentry/react';

// Benign browser transport/abort errors (offline, navigation away, CORS), anchored
// so we don't swallow real errors that merely contain the phrase. (ENG-2797)
const TRANSPORT_ERROR_PATTERNS: readonly RegExp[] = [
  /^Failed to fetch$/, // Chromium
  /^NetworkError when attempting to fetch resource\.?$/, // Firefox
  /^Load failed$/, // Safari / WebKit
  /^The network connection was lost\.?$/, // iOS WebKit
];

// Keep a small sample so a real backend outage still surfaces instead of going dark.
const TRANSPORT_ERROR_SAMPLE_RATE = 0.05;

/**
 * Whether a Sentry event for this error message is a benign transport error to
 * drop. Anchored matches are dropped ~95% of the time; the retained 5% keeps
 * outages visible. A message that only *contains* a pattern is never dropped.
 */
export function shouldDropTransportError(message: string | undefined): boolean {
  if (!message) {
    return false;
  }
  if (!TRANSPORT_ERROR_PATTERNS.some((pattern) => pattern.test(message))) {
    return false;
  }
  return Math.random() >= TRANSPORT_ERROR_SAMPLE_RATE;
}

/**
 * Sentry `beforeSend` that discards benign transport errors (returns null) and
 * keeps everything else. Shared by the web apps.
 */
export function dropTransportError(event: ErrorEvent): ErrorEvent | null {
  const message = event.exception?.values?.[0]?.value ?? event.message;
  return shouldDropTransportError(message) ? null : event;
}
